import React, { useCallback, useEffect, useState, WheelEvent } from "react";

import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons/faArrowLeft";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons/faArrowRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { chunk } from "lodash-es";

import AccessStatus from "@mapmycustomers/shared/types/entity/common/AccessStatus";
import { RawFile } from "@mapmycustomers/shared/types/File";
import { Button, Pagination, PaginationProps } from "@mapmycustomers/ui";

import useSizeObserver from "@app/util/hook/useSizeObserver";

import FileThumbnail from "../FileThumbnail";
import Preview from "../Preview";
import thumbnail from "../styles/_thumbnail.module.scss";

import styles from "./ViewableFileItems.module.scss";

const MOUSE_SCROLL_THRESHOLD = 5;

interface ViewableFileItemsProps {
  accessStatus?: AccessStatus;
  canDelete?: boolean;
  deleteTooltip?: string;
  editingTags?: boolean;
  files: RawFile[];
  onChange?: (file: RawFile) => void;
  onDelete?: (file: RawFile) => void;
  onDownload: (file: RawFile) => void;
  onFetchThumbnail?: (payload: { callback: (blob: Blob) => void; fileId: RawFile["id"] }) => void;
  size?: "default" | "small";
  supportsTags?: boolean;
}

const ViewableFileItems = ({
  accessStatus,
  canDelete = true,
  deleteTooltip,
  editingTags,
  files,
  onChange,
  onDelete,
  onDownload,
  onFetchThumbnail,
  size,
  supportsTags,
}: ViewableFileItemsProps) => {
  const [width, , ref] = useSizeObserver<HTMLDivElement>();

  const itemsPerPage = width
    ? Math.floor(
        width /
          parseInt(size === "small" ? thumbnail.smallThumbnailWidth : thumbnail.thumbnailWidth)
      )
    : 0;
  const totalPages = itemsPerPage ? Math.ceil(files.length / itemsPerPage) : 1;

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    // Avoid having currentPage bigger than totalPages when the width of the panel changes.
    // Between going to `totalPages` and `1` I decided to do the latter, it looked more natural.
    setCurrentPage((page) => (page <= totalPages ? page : 1));
  }, [totalPages]);

  const handlePrevPageClick = useCallback(() => {
    setCurrentPage((page) => (page > 1 ? page - 1 : page));
  }, []);

  const handleNextPageClick = useCallback(() => {
    setCurrentPage((page) => (page < totalPages ? page + 1 : page));
  }, [totalPages]);

  const handleMouseWheel = useCallback(
    (event: WheelEvent) => {
      if (event.deltaX > MOUSE_SCROLL_THRESHOLD) {
        handleNextPageClick();
      } else if (event.deltaX < -MOUSE_SCROLL_THRESHOLD) {
        handlePrevPageClick();
      }
    },
    [handleNextPageClick, handlePrevPageClick]
  );

  const handlePaginationRender = useCallback<NonNullable<PaginationProps["itemRender"]>>(
    (_, type, originalElement) => {
      // remove prev/next buttons
      if (type === "prev" || type === "next") {
        return null;
      }
      return originalElement;
    },
    []
  );

  const [selectedFile, setSelectedFile] = useState<RawFile | undefined>();
  const handleHidePreview = useCallback(() => {
    setSelectedFile(undefined);
  }, []);

  const buttonSize = size === "small" ? size : "middle";

  return (
    <div className={styles.container}>
      <div className={styles.gallery}>
        <Button
          disabled={currentPage <= 1}
          icon={<FontAwesomeIcon icon={faArrowLeft} />}
          iconOnly
          onClick={handlePrevPageClick}
          size={buttonSize}
          type="text"
        />
        <div className={cn(styles.pages, { [styles.editing]: editingTags })} ref={ref}>
          {chunk(files, itemsPerPage).map((pageFiles, pageIndex) => (
            <div
              className={styles.thumbnails}
              key={pageIndex}
              onWheel={handleMouseWheel}
              style={{
                left: (pageIndex - currentPage + 1) * width,
                right: (currentPage - pageIndex - 1) * width,
              }}
            >
              {pageFiles.map((file: RawFile) => (
                <FileThumbnail
                  accessStatus={accessStatus}
                  canDelete={canDelete}
                  className={styles.thumbnail}
                  deleteTooltip={deleteTooltip}
                  editingTags={editingTags}
                  file={file}
                  key={file.id}
                  onChange={onChange}
                  onClick={setSelectedFile}
                  onDelete={onDelete}
                  onDownload={onDownload}
                  onFetchThumbnail={onFetchThumbnail}
                  supportsTags={supportsTags}
                  visible={currentPage - 1 === pageIndex}
                />
              ))}
            </div>
          ))}
        </div>
        <Button
          disabled={currentPage >= totalPages}
          icon={<FontAwesomeIcon icon={faArrowRight} />}
          iconOnly
          onClick={handleNextPageClick}
          size={buttonSize}
          type="text"
        />
      </div>
      <Pagination
        className={styles.pagination}
        current={currentPage}
        hideOnSinglePage
        itemRender={handlePaginationRender}
        onChange={setCurrentPage}
        pageSize={itemsPerPage}
        showSizeChanger={false}
        size="small"
        total={totalPages * itemsPerPage}
      />
      <Preview
        files={files}
        // TODO: use fetchPreview instead of fetchThumbnail (although, it seem to have no difference...)
        onFetchPreview={onFetchThumbnail}
        onHide={handleHidePreview}
        selectedFile={selectedFile}
      />
    </div>
  );
};

export default ViewableFileItems;
